type Child = Node | string | number | null | undefined | false;
type Attrs = Record<string, string | number | boolean | ((ev: Event) => void) | null | undefined>;

/** Tiny hyperscript: h('button.primary', { onclick, 'data-testid': 'x' }, 'Go'). */
export function h(tag: string, attrs: Attrs = {}, ...children: (Child | Child[])[]): HTMLElement {
  const [name, ...classes] = tag.split('.');
  const el = document.createElement(name || 'div');
  if (classes.length) el.className = classes.join(' ');
  for (const [k, v] of Object.entries(attrs)) {
    if (v === null || v === undefined || v === false) continue;
    if (k.startsWith('on') && typeof v === 'function') el.addEventListener(k.slice(2), v as EventListener);
    else if (k === 'class') el.className = `${el.className} ${String(v)}`.trim();
    else if (v === true) el.setAttribute(k, '');
    else el.setAttribute(k, String(v));
  }
  for (const c of children.flat()) {
    if (c === null || c === undefined || c === false) continue;
    el.append(typeof c === 'string' || typeof c === 'number' ? document.createTextNode(String(c)) : c);
  }
  return el;
}

/** Cryptographically random id that also works on plain-http LAN origins (no randomUUID there). */
export function randomId(bytes = 16): string {
  const a = new Uint8Array(bytes);
  crypto.getRandomValues(a);
  return Array.from(a, (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * A button that asks "are you sure?" by needing a second tap within 4 seconds (FR-UX-17). Used in
 * place of window.confirm, which the iOS app's web view silently answers "no".
 */
export function confirmButton(tag: string, attrs: Attrs, label: string, ask: string, run: () => void): HTMLElement {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const button = h(tag, { ...attrs, type: 'button' }, label);
  button.addEventListener('click', () => {
    if (timer === undefined) {
      button.textContent = ask;
      button.classList.add('armed');
      timer = setTimeout(() => {
        timer = undefined;
        button.textContent = label;
        button.classList.remove('armed');
      }, 4000);
      return;
    }
    clearTimeout(timer);
    timer = undefined;
    run();
  });
  return button;
}
